import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { TextInput } from "@/components/ui/TextInput";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { PreviewSection, AddressEditor, AddressData, EmailEditor, EmailData } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ClientType } from "@/types/db/clients";
import { AbilitiesInput } from "@/components/inputs/AbilitiesInput";
import { RestrictionsInput } from "@/components/inputs/RestrictionsInput";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { ContactsInput } from "@/components/inputs/ContactsInput";
import useToast from "@/hooks/useToast";
import { supabase } from "@/lib/supabase-client";

const SAGE_ID_REGEX = /^C-\d{4}$/;

export interface ClientData {
  id: string;
  name: string;
  email?: string; // TODO: Legacy field - migrate to meta_data.emails
  address?: string;
  status: string;
  contact_person?: string;
  meta_data?: {
    address?: AddressData;
    emails?: EmailData[];
    phone?: string;
    industry?: string;
    [key: string]: any;
  };
  // Link table relationships
  client_abilities?: {
    ability: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  client_restrictions?: {
    restriction: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
}

export interface ClientOverviewTabProps {
  client: ClientData | null;
  loading?: boolean;
}

export function ClientOverviewTab({ client, loading = false }: ClientOverviewTabProps) {
  // Get edit context
  const { editing, formData, updateFormData } = useEntityEdit<ClientType>();
  const toast = useToast();

  // Sage ID validation
  const [sageIdError, setSageIdError] = useState<string | undefined>();
  useEffect(() => {
    if (!editing) {
      setSageIdError(undefined);
      return;
    }

    const sageId = formData?.meta_data?.sage_id?.trim();
    const originalSageId = client?.meta_data?.sage_id?.trim();

    if (!sageId || sageId === originalSageId) {
      setSageIdError(undefined);
      return;
    }

    if (!SAGE_ID_REGEX.test(sageId)) {
      setSageIdError("Format must be C-0000");
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("id")
          .eq("meta_data->>sage_id", sageId)
          .neq("id", client?.id || "")
          .limit(1);

        if (error) {
          console.error("Error checking Sage ID:", error);
          setSageIdError(undefined);
        } else if (data && data.length > 0) {
          setSageIdError("Sage ID already exists");
        } else {
          setSageIdError(undefined);
        }
      } catch (err) {
        console.error("Error checking Sage ID:", err);
        setSageIdError(undefined);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData?.meta_data?.sage_id, client?.meta_data?.sage_id, client?.id, editing]);

  // Unified data source - edit mode uses formData, view mode uses client
  // Cast to ClientData for consistent field access
  const displayData = (editing ? formData : client) as ClientData | null;

  // TODO: Fix payload on save to target meta_data.emails instead of top-level email field
  // Build emails array from meta_data.emails, falling back to legacy email field for migration
  const getEmailsArray = (): EmailData[] => {
    if (displayData?.meta_data?.emails && displayData.meta_data.emails.length > 0) {
      return displayData.meta_data.emails;
    }
    // Legacy support: convert old email field to emails array format
    if (displayData?.email) {
      return [{ name: "Primary", address: displayData.email }];
    }
    return [];
  };

  const handleAddEmail = () => {
    const currentEmails = formData?.meta_data?.emails || getEmailsArray();

    if (currentEmails.length >= 5) {
      toast.showToast({
        title: "Maximum emails reached",
        message: "You can add up to 5 email addresses",
        toastType: "warning",
      });
      return;
    }

    const newEmail: EmailData = {
      name: "",
      address: "",
    };

    updateFormData({
      ...formData,
      meta_data: {
        ...formData?.meta_data,
        emails: [...currentEmails, newEmail],
      },
    });
  };

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 16, padding: 16 }}>
        <PreviewSection
          title="Emails"
          button={
            editing
              ? {
                  icon: "plus",
                  onPress: handleAddEmail,
                  disabled: (displayData?.meta_data?.emails?.length || getEmailsArray().length) >= 5,
                }
              : undefined
          }
          editing={editing}
        >
          <EmailEditor
            emails={getEmailsArray()}
            onChange={(emails) =>
              updateFormData({
                ...formData,
                meta_data: {
                  ...formData?.meta_data,
                  emails,
                },
              })
            }
            editing={editing}
            loading={loading}
          />
        </PreviewSection>

        <PreviewSection title="Contact Information">
          <PhoneInput
            label="Phone"
            value={displayData?.meta_data?.phone || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                meta_data: {
                  ...formData?.meta_data,
                  phone: text,
                },
              })
            }
            editable={editing}
            loading={loading}
          />
          <TextInput
            label="Contact Person"
            value={displayData?.contact_person || ""}
            onChangeText={(text) => updateFormData({ ...formData, contact_person: text } as any)}
            editable={editing}
            loading={loading}
          />
        </PreviewSection>

        <PreviewSection title="Business Details">
          <TextInput
            label="Industry"
            value={displayData?.meta_data?.industry || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                meta_data: {
                  ...formData?.meta_data,
                  industry: text,
                },
              })
            }
            editable={editing}
            loading={loading}
          />
          <TextInput
            label="Sage ID"
            value={displayData?.meta_data?.sage_id || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                meta_data: {
                  ...formData?.meta_data,
                  sage_id: text,
                },
              })
            }
            placeholder="C-0000"
            error={sageIdError}
            editable={editing}
            loading={loading}
          />
          <AddressEditor
            address={displayData?.meta_data?.address}
            onChange={(address) =>
              updateFormData({
                ...formData,
                meta_data: { ...formData.meta_data, address },
              })
            }
            editing={editing}
            loading={loading}
          />
        </PreviewSection>

        <MarketsInput
          value={(displayData?.meta_data as any)?.markets || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(ids) =>
            updateFormData({
              ...formData,
              meta_data: { ...formData?.meta_data, markets: ids },
            } as any)
          }
        />

        <ContactsInput
          value={(displayData as any)?.client_contacts?.map((cc: any) => cc.contact) || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(contacts) =>
            updateFormData({
              ...formData,
              client_contacts: contacts.map((c) => ({ contact: c })),
            } as any)
          }
        />

        <AbilitiesInput
          value={(displayData?.client_abilities || []).map((ca) => ({ ability: ca.ability }))}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(abilities) => updateFormData({ ...formData, client_abilities: abilities } as any)}
        />

        <RestrictionsInput
          value={(displayData?.client_restrictions || []).map((cr) => ({ restriction: cr.restriction }))}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(restrictions) => updateFormData({ ...formData, client_restrictions: restrictions } as any)}
        />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
